import { Link } from "react-router-dom";
import { SacredFlameMark } from "@/components/Brand";

const pillars = [
  { icon: "fa-eye", t: "Awareness", d: "Gently noticing the patterns and triggers that operate below conscious effort." },
  { icon: "fa-compass", t: "Alignment", d: "Bringing your deeper values and your everyday choices into the same direction." },
  { icon: "fa-seedling", t: "Reframing", d: "Working with the subconscious mind to soften old stories and open new ones." },
  { icon: "fa-fire", t: "Integration", d: "Letting change settle into daily life, supported by faith, community, and care." },
];

export default function SacredFlame() {
  return (
    <div className="bg-[#1C1917] text-stone-200" data-testid="sacred-flame-page">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(70% 90% at 20% 10%, rgba(217,119,6,0.30), transparent 60%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-16 sm:pt-24 pb-16">
          <SacredFlameMark />
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl text-amber-50 mt-8 leading-[1.05] max-w-3xl">
            The Sacred Flame Methodology
          </h1>
          <p className="text-lg text-stone-300 mt-6 max-w-2xl leading-relaxed">
            A faith-informed framework that treats change as a return to something already true in
            you. It brings heart, mind, and habit into alignment — gently, and without shame.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-9">
            <Link
              to="/assessments/sacred-flame-readiness"
              data-testid="sacred-flame-assessment-cta"
              className="rounded-full px-7 py-4 text-sm font-semibold bg-amber-500 text-stone-900 hover:bg-amber-400 transition-colors text-center"
            >
              Take the Sacred Flame Readiness Assessment
            </Link>
            <Link
              to="/consultation"
              data-testid="sacred-flame-consultation-cta"
              className="rounded-full px-7 py-4 text-sm font-semibold border border-amber-500/40 text-amber-100 hover:bg-amber-500/10 transition-colors text-center"
            >
              Request a free consultation
            </Link>
          </div>
        </div>
      </div>

      {/* Pillars */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-20">
        <p className="text-amber-300/70 font-mono text-xs tracking-[0.2em] uppercase">Four movements</p>
        <h2 className="font-serif-display text-4xl text-amber-50 mt-3">The shape of the work</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {pillars.map((p, i) => (
            <div
              key={p.t}
              className="rounded-2xl bg-[#262320] border border-amber-500/20 p-7 hover:border-amber-500/50 transition-colors"
            >
              <div className="grid place-items-center h-12 w-12 rounded-full bg-gradient-to-b from-amber-500/30 to-rose-900/20 border border-amber-500/40">
                <i className={`fa-solid ${p.icon} text-amber-400`} />
              </div>
              <p className="font-mono text-xs text-amber-400/60 mt-5">0{i + 1}</p>
              <h3 className="font-serif-display text-2xl text-amber-50 mt-1">{p.t}</h3>
              <p className="text-stone-400 text-sm mt-2 leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 rounded-[2rem] border border-amber-500/20 bg-[#262320] p-8 sm:p-12">
          <h3 className="font-serif-display text-3xl text-amber-50">A word on what this is — and isn't</h3>
          <p className="text-stone-300 mt-4 max-w-3xl leading-relaxed">
            Sacred Flame is a supportive, reflective framework. It is not a medical treatment, a cure,
            or a substitute for professional care. For many people it may help create space for hope
            and change; it works best alongside appropriate clinical, medical, and community support.
          </p>
          <p className="text-stone-500 text-xs mt-4">
            Faith elements are always offered, never imposed. You are welcome here exactly as you are.
          </p>
        </div>
      </div>
    </div>
  );
}
